import {
  useState,
} from 'react';
import {
  Callout,
  Field,
  FilterBar,
  Modal,
  SearchRow,
  Select,
  TableActions,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  useLoad,
  countryLabel,
  rowOpen,
  localDayOf,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  lifecycleLabel,
} from '../../screens.ts';
import { t } from '../../messages.ts';
import { useAbilities } from '../abilities.tsx';
import { operatorMatches, type OperatorStateFilter } from '../entity-actions.ts';
import { Failure } from '../shared.tsx';
import { AddOperator } from './AddOperator.tsx';
import { OperatorDetail } from './OperatorDetail.tsx';

export function Operators({ api }: { api: PlatformApi }) {
  const { can } = useAbilities();
  const [stateFilter, setStateFilter] = useState<OperatorStateFilter>('all');
  const [version, setVersion] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  /** Whether the open detail panel changed something the list shows. */
  const [detailChanged, setDetailChanged] = useState(false);
  const loaded = useLoad(() => api.operators(query || undefined), [query, version]);
  // Batch 2 item 9 — a search used to swap the whole screen, title, search
  // box and all, for a skeleton, which dropped focus out of the box on every
  // keystroke's result. The head and search stay mounted; only the panel's
  // own content loads.
  const openRow = loaded.state === 'ready' ? loaded.value.items.find((o) => o.id === open) : undefined;
  const shown = loaded.state === 'ready' ? loaded.value.items.filter((o) => operatorMatches(o, stateFilter)) : [];

  return (
    <>
      <div className="page-head">
        <h1>{t('ops.list.title')}</h1>
        {can.edit ? (
          <div className="page-head-actions">
            <button className="primary" onClick={() => setAdding(true)}>{t('ops.list.add')}</button>
          </div>
        ) : null}
      </div>

      {adding ? (
        <Modal title={t('ops.list.addTitle')} onClose={() => setAdding(false)}>
          <AddOperator
            api={api}
            onDone={() => {
              setAdding(false);
              setVersion((v) => v + 1);
            }}
          />
        </Modal>
      ) : null}

      <SearchRow
        value={searchInput}
        onChange={setSearchInput}
        onSearch={() => setQuery(searchInput.trim())}
        placeholder={t('ops.list.searchPlaceholder')}
      />

      <FilterBar>
        <Field label={t('common.status')}>
          <Select
            value={stateFilter}
            onChange={(v) => setStateFilter(v as OperatorStateFilter)}
            options={[
              { value: 'all', label: t('ent.operators.filter.all') },
              { value: 'live', label: t('ent.operators.filter.live') },
              { value: 'setup', label: t('ent.operators.filter.setup') },
              { value: 'suspended', label: t('ent.operators.filter.suspended') },
              { value: 'awaiting', label: t('ent.operators.filter.awaiting') },
              { value: 'deleted', label: t('ent.operators.filter.deleted') },
            ]}
          />
        </Field>
      </FilterBar>

      {loaded.state === 'loading' ? <Loading rows={2} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

      {loaded.state === 'ready' ? (
      <Panel
        title={t('ops.list.panelTitle')}
        aside={
          <div className="panel-aside">
            <span className="count count-badge">{shown.length}</span>
            <TableActions
              title={t('ops.list.title')}
              filename="operators"
              headers={[
                t('common.operator'), t('ops.list.legalName'), t('ops.list.admin'), t('ops.profile.adminEmail'),
                t('common.status'), t('common.currency'), t('common.country'),
                t('ops.list.brands'), t('ops.list.affiliates'), t('ops.list.joined'),
              ]}
              keys={[
                'operator', 'legal_name', 'admin', 'admin_email', 'status', 'currency', 'country',
                'brands', 'affiliates', 'joined',
              ]}
              rows={shown.map((o) => [
                o.display_name, o.legal_name, o.owner_name ?? '', o.owner_email ?? '',
                lifecycleLabel(o.lifecycle_state), o.base_currency, o.country ?? '',
                String(o.brands), String(o.affiliates), localDayOf(o.joined),
              ])}
            />
          </div>
        }
      >
        {shown.length === 0 ? (
          <Empty>{query || stateFilter !== 'all' ? t('common.nobodyMatches') : t('ops.list.none')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('common.operator')}</th><th>{t('ops.list.legalName')}</th><th>{t('ops.list.admin')}</th><th>{t('common.status')}</th><th>{t('common.currency')}</th>
                  <th>{t('common.country')}</th><th className="num">{t('ops.list.brands')}</th><th className="num">{t('ops.list.affiliates')}</th>
                  <th className="num">{t('ops.list.joined')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((o) => {
                  const openThis = () => setOpen(o.id);
                  return (
                  <tr key={o.id} className="row-clickable" onClick={openThis} {...rowOpen(openThis)}>
                    <td>{o.display_name}</td>
                    <td className="faint">{o.legal_name}</td>
                    <td className="faint stack">
                      {o.owner_name ? <div title={o.owner_name}>{o.owner_name}</div> : null}
                      {o.owner_email ? <div title={o.owner_email}>{o.owner_email}</div> : null}
                      {o.owner_phone ? <div title={o.owner_phone}>{o.owner_phone}</div> : null}
                      {!o.owner_name && !o.owner_email && !o.owner_phone ? '—' : null}
                    </td>
                    <td>
                      {o.lifecycle_state === 'PURGED' || o.lifecycle_state === 'SOFT_DELETED' ? (
                        /* Batch 2 item 6 — soft-deleted is not a healthy,
                           settled state (it was locked/green); neutral, like
                           every other "off" state this console has. */
                        <Chip tone={o.lifecycle_state === 'PURGED' ? 'overdue' : 'neutral'}>
                          {lifecycleLabel(o.lifecycle_state)}
                        </Chip>
                      ) : (
                        lifecycleLabel(o.lifecycle_state)
                      )}
                      {/* Migration 0111 — a self-signup's approval gate is separate from
                          lifecycle state, so it needs its own badge when it isn't the
                          unremarkable default.

                          Batch 2 item 8 — `!== 'APPROVED'` also caught a
                          missing value (an operator with no gate at all) and
                          called it "Awaiting approval", which was never true
                          for one. Only the two states that actually mean
                          something show a chip now. */}
                      {o.approval_status === 'PENDING' || o.approval_status === 'REJECTED' ? (
                        <div>
                          <Chip tone={o.approval_status === 'REJECTED' ? 'overdue' : 'provisional'}>
                            {o.approval_status === 'REJECTED' ? t('ops.list.rejected') : t('ops.list.awaiting')}
                          </Chip>
                        </div>
                      ) : null}
                    </td>
                    <td className="num faint">{o.base_currency}</td>
                    <td>{countryLabel(o.country)}</td>
                    <td className="num">{o.brands}</td>
                    <td className="num">{o.affiliates}</td>
                    <td className="num faint">{formatDate(o.joined)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
      ) : null}

      {open ? (
        <Modal
          wide
          // Batch 2 item 10 — every one of the popup's 10 sections carried
          // "— go-live gate" in its title, which only ever described one of
          // them. The name is the title; the status (when the row is at
          // hand) sits beside it instead of inside the string.
          title={openRow?.display_name ?? ''}
          /*
           * Refreshed on the way out, rather than the moment the panel changes
           * something. `useLoad` reports `loading` again on every refetch and
           * this screen renders a placeholder in that state, so bumping the
           * list while the panel is open would pull the panel out from under
           * whoever is working in it. Closing is the first moment the list is
           * what they are looking at.
           */
          onClose={() => {
            setOpen(null);
            if (detailChanged) {
              setDetailChanged(false);
              setVersion((v) => v + 1);
            }
          }}
        >
          <OperatorDetail
            api={api}
            operatorId={open}
            owner={openRow ?? null}
            onChanged={() => setDetailChanged(true)}
            onDeleted={() => {
              setOpen(null);
              setDetailChanged(false);
              setVersion((v) => v + 1);
            }}
          />
        </Modal>
      ) : null}

      {/* §3.9e / E30 (2026-08-20) — platform staff MAY read every tenant's
          affiliates, deals and commission (§3.9a); this list stays names,
          status and counts on purpose. A deeper read is a purpose-built,
          operator_id-scoped screen elsewhere in this console, with its own
          per-access audit trail — never a session that behaves like this
          operator's own login (§3.10a forbids affdice-superadmin and affdice-admin
          sharing one). See docs/delivery/console-parity-ticket.md §2. */}
      <Callout>
        {t('ops.list.note')}
      </Callout>
    </>
  );
}
